import React, { useEffect, useRef, useState } from 'react';
import Sidebar from '../components/Sidebar';
import './Documentation.css';

/* ══════════════════════════════════════════
   DONNÉES
══════════════════════════════════════════ */
const STEPS = [
  {
    id: 'auth',
    icon: 'fa-user-lock',
    color: '#667eea',
    bg: 'rgba(102,126,234,0.1)',
    title: 'Connexion & Compte',
    tag: 'Point de départ',
    description: "Créez votre compte ou connectez-vous. La session est sécurisée par un token JWT valable 24h — la déconnexion est automatique à expiration.",
    items: [
      { icon: 'fa-user-plus',   text: 'Créer un compte avec email + mot de passe' },
      { icon: 'fa-sign-in-alt', text: 'Connexion — token JWT généré automatiquement' },
      { icon: 'fa-shield-alt',  text: 'Session expirée → déconnexion automatique' },
    ],
    tip: 'Activez la 2FA dans Paramètres → Sécurité pour plus de protection.',
  },
  {
    id: 'settings',
    icon: 'fa-cog',
    color: '#f97316',
    bg: 'rgba(249,115,22,0.1)',
    title: 'Paramètres',
    tag: 'À configurer en premier',
    description: "Avant de créer des documents, configurez la numérotation automatique, votre devise et votre langue.",
    items: [
      { icon: 'fa-hashtag',  text: 'Préfixes et numéros de départ (INV-2026-001)' },
      { icon: 'fa-coins',    text: 'Devise par défaut et langue de l\'interface' },
      { icon: 'fa-palette',  text: 'Thème clair/sombre + couleur d\'accentuation' },
    ],
    tip: 'Le numéro s\'incrémente automatiquement après chaque PDF généré.',
  },
  {
    id: 'clients',
    icon: 'fa-users',
    color: '#10b981',
    bg: 'rgba(16,185,129,0.1)',
    title: 'Gestion des clients',
    tag: 'Votre carnet d\'adresses',
    description: "Créez votre base clients. Ils sont ensuite disponibles via l'autocomplete sur toutes vos factures et devis.",
    items: [
      { icon: 'fa-user-plus',    text: 'Ajouter des particuliers et des sociétés' },
      { icon: 'fa-search',       text: 'Rechercher et filtrer par type' },
      { icon: 'fa-file-invoice', text: 'Créer une facture ou un devis depuis la fiche' },
    ],
    tip: 'Vous pouvez aussi sauvegarder un client directement depuis le formulaire de facture.',
  },
  {
    id: 'invoice',
    icon: 'fa-file-invoice',
    color: '#3b82f6',
    bg: 'rgba(59,130,246,0.1)',
    title: 'Créer une facture',
    tag: 'Facturation professionnelle',
    description: "Renseignez vos infos entreprise, sélectionnez un client, ajoutez vos articles. Le PDF est identique à l'aperçu.",
    items: [
      { icon: 'fa-paint-brush', text: 'Choisir couleur, logo et template' },
      { icon: 'fa-list',        text: 'Ajouter articles avec quantité et prix unitaire' },
      { icon: 'fa-wallet',      text: 'Sélectionner mode(s) de paiement acceptés' },
      { icon: 'fa-file-pdf',    text: 'Aperçu en temps réel → export PDF identique' },
    ],
    tip: 'La signature est optionnelle — activez le toggle pour l\'inclure dans le PDF.',
  },
  {
    id: 'devis',
    icon: 'fa-file-contract',
    color: '#8b5cf6',
    bg: 'rgba(139,92,246,0.1)',
    title: 'Créer un devis',
    tag: 'Propositions commerciales',
    description: "Créez des devis avec statut, options facultatives, acompte et date de validité. Un devis accepté se convertit en facture en 1 clic.",
    items: [
      { icon: 'fa-tag',          text: 'Statut : Brouillon, Envoyé, Accepté, Refusé' },
      { icon: 'fa-puzzle-piece', text: 'Options facultatives non incluses dans le total' },
      { icon: 'fa-percent',      text: 'Acompte en pourcentage du montant TTC' },
      { icon: 'fa-exchange-alt', text: 'Convertir en facture si le devis est accepté' },
    ],
    tip: 'Depuis l\'aperçu, cliquez "Convertir en facture" si le devis est accepté.',
  },
  {
    id: 'historique',
    icon: 'fa-history',
    color: '#f43f5e',
    bg: 'rgba(244,63,94,0.1)',
    title: 'Historique',
    tag: 'Tous vos documents',
    description: "Retrouvez toutes vos factures et devis sauvegardés. Éditez, modifiez et régénérez à tout moment.",
    items: [
      { icon: 'fa-archive',  text: 'Factures et devis enregistrés dans un seul onglet' },
      { icon: 'fa-edit',     text: 'Éditer — rechargement du formulaire en 1 clic' },
      { icon: 'fa-trash',    text: 'Supprimer les documents inutiles' },
    ],
    tip: 'Les documents apparaissent automatiquement après chaque téléchargement PDF.',
  },
  {
    id: 'cv',
    icon: 'fa-id-card',
    color: '#ec4899',
    bg: 'rgba(236,72,153,0.1)',
    title: 'CV Intelligent',
    tag: 'IA + Score ATS',
    description: "Créez votre CV ou uploadez un existant. L'IA l'adapte à une offre d'emploi et génère une lettre de motivation personnalisée.",
    items: [
      { icon: 'fa-magic',              text: 'Créer un CV : templates, couleurs, PDF/Word' },
      { icon: 'fa-cloud-upload-alt',   text: 'Uploader un CV PDF ou DOCX existant' },
      { icon: 'fa-chart-bar',          text: 'Score ATS calculé sur 9 critères' },
      { icon: 'fa-envelope-open-text', text: 'Adapter à une offre + lettre de motivation IA' },
    ],
    tip: 'Utilisez un .docx pour une meilleure extraction de texte et un meilleur score ATS.',
  },
  {
    id: 'crm',
    icon: 'fa-handshake',
    color: '#14b8a6',
    bg: 'rgba(20,184,166,0.1)',
    title: 'CRM',
    tag: 'Suivi commercial',
    description: "Gérez votre pipeline commercial avec un Kanban d'opportunités et un suivi d'activités avec alertes automatiques.",
    items: [
      { icon: 'fa-columns',  text: 'Kanban 5 étapes : Prospect → Clôture' },
      { icon: 'fa-tasks',    text: 'Activités avec date, heure et responsable' },
      { icon: 'fa-bell',     text: 'Alertes : Aujourd\'hui / En retard / À venir' },
      { icon: 'fa-download', text: 'Exporter les activités en PDF, CSV ou JSON' },
    ],
    tip: 'Les activités en retard sont automatiquement signalées en rouge.',
  },
];

/* ══════════════════════════════════════════
   HOOK — IntersectionObserver fiable
   threshold:0 = déclenche dès le 1er pixel visible
══════════════════════════════════════════ */
const useInView = () => {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    // Fallback: si l'élément est déjà dans le viewport
    const rect = el.getBoundingClientRect();
    if (rect.top < window.innerHeight) { setVisible(true); return; }

    const obs = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setVisible(true); obs.disconnect(); } },
      { threshold: 0, rootMargin: '0px 0px -40px 0px' }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);
  return [ref, visible];
};

/* ══════════════════════════════════════════
   CARTE ÉTAPE
══════════════════════════════════════════ */
const StepCard = ({ step, index }) => {
  const [ref, visible] = useInView();
  const isEven = index % 2 === 0;

  return (
    <div
      ref={ref}
      className={`doc-card ${isEven ? 'doc-card--even' : 'doc-card--odd'} ${visible ? 'doc-card--visible' : ''}`}
      style={{ transitionDelay: `${index * 0.05}s` }}
      id={step.id}
    >
      {/* Spacer côté vide */}
      <div className="doc-card-spacer" />

      {/* Point sur la ligne */}
      <div className="doc-card-axis">
        <div className="doc-card-dot" style={{ background: step.color, boxShadow: `0 0 0 5px ${step.color}20` }}>
          <i className={`fas ${step.icon}`} />
        </div>
      </div>

      {/* Contenu */}
      <div className="doc-card-content" style={{ '--c': step.color, '--cbg': step.bg }}>
        <div className="doc-card-tag" style={{ color: step.color, background: step.bg }}>
          <span className="doc-card-index">{String(index + 1).padStart(2, '0')}</span>
          {step.tag}
        </div>
        <h3 className="doc-card-h3">{step.title}</h3>
        <p className="doc-card-p">{step.description}</p>

        <ul className="doc-card-ul">
          {step.items.map((item, i) => (
            <li key={i} className="doc-card-li">
              <span className="doc-card-li-icon" style={{ color: step.color, background: step.bg }}>
                <i className={`fas ${item.icon}`} />
              </span>
              {item.text}
            </li>
          ))}
        </ul>

        {step.tip && (
          <div className="doc-card-tip" style={{ borderLeftColor: step.color, background: step.bg }}>
            <i className="fas fa-lightbulb" style={{ color: step.color }} />
            <span>{step.tip}</span>
          </div>
        )}
      </div>
    </div>
  );
};

/* ══════════════════════════════════════════
   PAGE PRINCIPALE
══════════════════════════════════════════ */
const Documentation = () => {
  const [scrollY, setScrollY] = useState(0);

  useEffect(() => {
    const h = () => setScrollY(window.scrollY);
    window.addEventListener('scroll', h, { passive: true });
    return () => window.removeEventListener('scroll', h);
  }, []);

  return (
    <div className="page-with-sidebar">
      <Sidebar />
      <main className="doc-main">

        {/* ══ HERO ══ */}
        <section className="doc-hero">
          <div className="doc-orb doc-orb--1" />
          <div className="doc-orb doc-orb--2" />
          <div className="doc-orb doc-orb--3" />
          <div className="doc-grid-bg" />

          <div className="doc-hero-inner" style={{ transform: `translateY(${scrollY * 0.12}px)` }}>
            <div className="doc-hero-pill">
              <i className="fas fa-book-open" /> Guide d'utilisation
            </div>
            <h1 className="doc-hero-title">
              Comment utiliser<br />
              <span className="doc-hero-accent">FlowGenerate</span>
            </h1>
            <p className="doc-hero-desc">
              Suivez ce guide étape par étape pour maîtriser toutes les fonctionnalités — de la création de compte jusqu'à l'export de vos documents.
            </p>

            <div className="doc-hero-stats">
              {[
                { icon: 'fa-file-invoice',  label: 'Factures & Devis' },
                { icon: 'fa-id-card',       label: 'CV avec IA' },
                { icon: 'fa-handshake',     label: 'CRM intégré' },
                { icon: 'fa-shield-alt',    label: 'Auth sécurisée' },
              ].map((s, i) => (
                <div key={i} className="doc-hero-stat" style={{ animationDelay: `${0.3 + i * 0.07}s` }}>
                  <i className={`fas ${s.icon}`} />
                  <span>{s.label}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Navigation rapide */}
          <nav className="doc-nav">
            {STEPS.map(s => (
              <a key={s.id} href={`#${s.id}`} className="doc-nav-item" style={{ '--c': s.color }}>
                <i className={`fas ${s.icon}`} />
                <span>{s.title}</span>
              </a>
            ))}
          </nav>
        </section>

        {/* ══ TIMELINE ══ */}
        <section className="doc-timeline">
          <div className="doc-timeline-line" />
          {STEPS.map((step, i) => (
            <StepCard key={step.id} step={step} index={i} />
          ))}

          {/* Fin */}
          <div className="doc-finish">
            <div className="doc-finish-dot">
              <i className="fas fa-check" />
            </div>
            <strong>Vous êtes prêt !</strong>
            <span>Toutes les fonctionnalités sont accessibles depuis la barre latérale.</span>
          </div>
        </section>

        {/* ══ CTA ══ */}
        <section className="doc-cta-wrap">
          <div className="doc-cta">
            <div className="doc-cta-glow" />
            <i className="fas fa-rocket doc-cta-rocket" />
            <h2>Commencer maintenant</h2>
            <p>Créez votre premier document professionnel en quelques minutes.</p>
            <div className="doc-cta-actions">
              <a href="/invoice" className="doc-btn doc-btn--primary">
                <i className="fas fa-file-invoice" /> Nouvelle facture
              </a>
              <a href="/cv" className="doc-btn doc-btn--ghost">
                <i className="fas fa-id-card" /> Créer un CV
              </a>
            </div>
          </div>
        </section>

      </main>
    </div>
  );
};

export default Documentation;